import { PAYMENT_METHODS } from 'my-phorest/utils/payment-balance';

/**
 * When checking out, we ask for the client's current account balance.
 * That information is not held in the calendar as `appointment.client.creditAccount.outstandingBalance` (as in a basket),
 * so we don't get the balance update automatically from Apollo. It's kept as `appointment.clientCreditAccountBalance` instead.
 *
 * Here, we manually update the appointment's field with the correlated client's account.
 *
 * The update doesn't happen if:
 *  - client doesn't have the credit account balance
 *  - no operation regarding credit balance was done (no money in, no money out)
 *  - no appointments for this client were found in the calendar
 *
 * @param {ApolloCache} cache
 * @param {object} basket
 */
export function maybeUpdateClientCreditAccountBalanceForBasket(cache, basket) {
  if (!basket.client.creditAccount?.outstandingBalance) return;

  const addedCreditAccountMoney = basket.items.some(
    (item) =>
      item.__typename === 'CreditAccountBasketItem' ||
      item.__typename === 'CreditAccountAppointmentDepositBasketItem'
  );
  const spentCreditAccountMoney = basket.payments.some(
    (item) =>
      item.__typename === 'BasketNonIntegratedPayment' &&
      item.paymentType === PAYMENT_METHODS.ACCOUNT
  );

  if (!addedCreditAccountMoney && !spentCreditAccountMoney) return;

  updateClientCreditAccountBalanceInAppointments(
    cache,
    basket.client.id,
    basket.client.creditAccount.outstandingBalance
  );
}

/**
 * Updates client credit account balance in appointments. The operation is done in the Apollo cache.
 *
 * @param {ApolloCache} cache
 * @param {string} clientId
 * @param {string} newBalance - a decimal value, like "1.50"
 */
export function updateClientCreditAccountBalanceInAppointments(
  cache,
  clientId,
  newBalance
) {
  const clientRef = cache.identify({
    id: clientId,
    __typename: 'Client',
  });
  const extractedCache = cache.extract();
  const clientAppointments = Object.values(extractedCache).filter(
    (item) =>
      item.__typename === 'Appointment' && item.client?.__ref === clientRef
  );
  clientAppointments.forEach((appointment) => {
    if (appointment.clientCreditAccountBalance) {
      /* Note on the types and conversion:
          - `appointment.clientCreditAccountBalance` is `Monetary`. Which means, `amount` is `Float!` (e.g. 1.5)
          - `client.creditAccount.outstandingBalance` is (unfortunately?) `Decimal!` (e.g. "1.50")

         It would be better to have both as `Monetary` or at least have the `client.creditAccount.outstandingBalance` as `Money`,
         but it is what it is.
       */
      cache.modify({
        id: cache.identify({ id: appointment.id, __typename: 'Appointment' }),
        fields: {
          clientCreditAccountBalance(currentValue = {}) {
            return {
              ...currentValue,
              amount: parseFloat(newBalance),
            };
          },
        },
      });
    }
  });
}
